import type { TrackingEvent, TrackingEventData } from '@/types/tracking'
import React, { useCallback } from 'react'

type TrackingService = {
	sendEvent: <T extends TrackingEvent>(eventName: T, data?: TrackingEventData<T>) => void
}

type TrackingContextType = {
	services: TrackingService[]
	sendEvent: TrackingService['sendEvent']
}

const TrackingContext = React.createContext<TrackingContextType | undefined>(undefined)

export function useTracking() {
	const context = React.useContext(TrackingContext)

	if (!context) {
		throw new Error('useTracking must be used within a TrackingProvider')
	}

	return context
}

export function TrackingProvider({ services, children }: { services: TrackingService[]; children: React.ReactNode }) {
	const sendEvent = useCallback(
		<T extends TrackingEvent>(eventName: T, data?: TrackingEventData<T>) => {
			services.forEach(service => {
				service.sendEvent(eventName, data)
			})
		},
		[services]
	)

	return <TrackingContext.Provider value={{ services, sendEvent }}>{children}</TrackingContext.Provider>
}
